import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentsService, Subscription, Payment } from '../services/payments.service';
import { useAuth } from '../hooks/useAuth';

export const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isManaging, setIsManaging] = useState(false);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [subData, paymentsData] = await Promise.all([
        paymentsService.getSubscription(),
        paymentsService.getPaymentHistory(),
      ]);
      setSubscription(subData);
      setPayments(paymentsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load subscription data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsManaging(true);
    setError(null);

    try {
      const { url } = await paymentsService.createPortalSession();
      // Redirect to Stripe Customer Portal
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to open subscription portal');
      setIsManaging(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      active: '#10b981',
      trialing: '#3b82f6',
      past_due: '#f59e0b',
      canceled: '#ef4444',
      succeeded: '#10b981',
      failed: '#ef4444',
      pending: '#f59e0b',
    };

    return (
      <span
        style={{
          ...styles.badge,
          backgroundColor: statusColors[status] || '#6b7280',
        }}
      >
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingCard}>
          <p>Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (!user?.isPremium) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>No Active Subscription</h1>
          <p style={styles.message}>
            You don't have an active premium subscription yet.
          </p>
          <button
            onClick={() => navigate('/premium')}
            style={styles.button}
          >
            Upgrade to Premium
          </button>
          <button onClick={handleGoBack} style={styles.secondaryButton}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Header */}
        <div style={styles.header}>
          <button onClick={handleGoBack} style={styles.backButton}>
            ← Back
          </button>
          <h1 style={styles.pageTitle}>Subscription Management</h1>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* Subscription Details */}
        <div style={styles.card}>
          <h2 style={styles.sectionTitle}>Subscription Details</h2>

          {subscription ? (
            <div style={styles.detailsGrid}>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Status:</span>
                <span>{getStatusBadge(subscription.status)}</span>
              </div>

              {subscription.currentPeriodStart && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Current Period Start:</span>
                  <span>{formatDate(subscription.currentPeriodStart)}</span>
                </div>
              )}

              {subscription.currentPeriodEnd && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Current Period End:</span>
                  <span>{formatDate(subscription.currentPeriodEnd)}</span>
                </div>
              )}

              {subscription.cancelAtPeriodEnd && (
                <div style={styles.warningBox}>
                  Your subscription will cancel at the end of the current billing period.
                </div>
              )}
            </div>
          ) : (
            <p>No subscription details available.</p>
          )}

          <button
            onClick={handleManageSubscription}
            disabled={isManaging}
            style={{
              ...styles.button,
              marginTop: '1.5rem',
              opacity: isManaging ? 0.6 : 1,
              cursor: isManaging ? 'not-allowed' : 'pointer',
            }}
          >
            {isManaging ? 'Opening Portal...' : 'Manage Subscription'}
          </button>
          <p style={styles.helperText}>
            Update payment method, cancel subscription, or view invoices
          </p>
        </div>

        {/* Billing History */}
        <div style={styles.card}>
          <h2 style={styles.sectionTitle}>Billing History</h2>

          {payments.length > 0 ? (
            <div style={styles.paymentsTable}>
              {payments.map((payment) => (
                <div key={payment.id} style={styles.paymentRow}>
                  <div style={styles.paymentInfo}>
                    <div style={styles.paymentDate}>
                      {formatDate(payment.createdAt)}
                    </div>
                    <div style={styles.paymentDescription}>
                      {payment.description || 'Subscription payment'}
                    </div>
                  </div>
                  <div style={styles.paymentAmount}>
                    {formatAmount(payment.amount, payment.currency)}
                  </div>
                  <div style={styles.paymentStatus}>
                    {getStatusBadge(payment.status)}
                  </div>
                  {payment.receiptUrl && (
                    <a
                      href={payment.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.receiptLink}
                    >
                      View Receipt
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={styles.emptyMessage}>No payment history available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-soft-cream)',
    padding: '2rem',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  loadingCard: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '3rem',
    textAlign: 'center',
    boxShadow: 'var(--shadow-md)',
  },
  header: {
    position: 'relative',
    marginBottom: '2rem',
    textAlign: 'center',
  },
  backButton: {
    position: 'absolute',
    left: 0,
    top: 0,
    padding: '0.5rem 1rem',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '1rem',
    color: 'var(--color-dark-navy)',
    fontWeight: '500',
  },
  pageTitle: {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
    margin: 0,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    marginBottom: '2rem',
    boxShadow: 'var(--shadow-md)',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: 'var(--color-deep-purple)',
    marginBottom: '1.5rem',
    margin: '0 0 1.5rem 0',
  },
  detailsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem',
    backgroundColor: 'var(--color-gray-50)',
    borderRadius: 'var(--radius-md)',
  },
  detailLabel: {
    fontWeight: '600',
    color: 'var(--color-gray-700)',
  },
  badge: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius-sm)',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  warningBox: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    padding: '1rem',
    borderRadius: 'var(--radius-md)',
    marginTop: '1rem',
    fontSize: '0.9rem',
  },
  button: {
    width: '100%',
    padding: '1rem',
    backgroundColor: 'var(--color-warm-orange)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  secondaryButton: {
    width: '100%',
    padding: '1rem',
    backgroundColor: 'var(--color-gray-200)',
    color: 'var(--color-dark-navy)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '1rem',
    transition: 'background-color 0.2s',
  },
  helperText: {
    fontSize: '0.875rem',
    color: 'var(--color-gray-500)',
    textAlign: 'center',
    marginTop: '0.5rem',
    marginBottom: 0,
  },
  paymentsTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  paymentRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem',
    backgroundColor: 'var(--color-gray-50)',
    borderRadius: 'var(--radius-md)',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  paymentInfo: {
    flex: '1 1 200px',
  },
  paymentDate: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
  },
  paymentDescription: {
    fontSize: '0.85rem',
    color: 'var(--color-gray-500)',
  },
  paymentAmount: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--color-dark-navy)',
  },
  paymentStatus: {
    minWidth: '100px',
  },
  receiptLink: {
    fontSize: '0.875rem',
    color: 'var(--color-soft-blue)',
    textDecoration: 'none',
    fontWeight: '500',
  },
  emptyMessage: {
    textAlign: 'center',
    color: 'var(--color-gray-500)',
    fontSize: '1rem',
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '1rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
    marginBottom: '1rem',
  },
  message: {
    fontSize: '1.1rem',
    color: 'var(--color-gray-600)',
    marginBottom: '2rem',
    lineHeight: '1.6',
  },
};
